import React, { useEffect, useMemo, useRef, useState } from "react";
import { LoadingState } from "@/components/brand";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlignLeft,
  ChevronsUpDown,
  ClipboardList,
  FileText,
  Folder,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
  type LucideIcon
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { IconTile } from "@/components/ui/icon-tile";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { PdfPreview } from "../../../components/common/PdfPreview";
import { DocumentsAPI } from "../../../api/documentsApi";
import { CasePrepUscisFormsAPI } from "../api";
import { SourceTypeTag } from "./fieldSource/SourceTypeTag";
import type {
  CasePrepEvidenceSource,
  CasePrepIntakeSource,
  CasePrepIntakeSubmissionSource,
  CasePrepUscisSource,
  CasePrepUscisSourceType,
  CasePrepUscisSourcesResponse
} from "../types";
import {
  isRecommendedSource,
  MAX_FORM_SOURCES,
  reviewStatusLabel,
  sourceCardLabel,
  sourceRecommendationReason,
  sourceRenderKey,
  sourceStepLeadIn,
  stringifyValue
} from "../utils";

type SourceSelectOption = {
  value: string;
  label: string;
  exhibit?: string | null;
  isDisabled?: boolean;
  intakeTitle?: string;
  reviewStatus?: string;
  notReady?: boolean;
};

export type SourceListEditorStatus = {
  isLoadingSources: boolean;
  isRecommendingSources: boolean;
  /** An add or edit card is open, so the list is mid-edit and not ready to run. */
  hasOpenDraft: boolean;
};

const EMPTY_CANDIDATE_SOURCES: CasePrepUscisSourcesResponse = {
  evidence_documents: [],
  case_files: [],
  intakes: []
};

export type SearchableSelectOption = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

/**
 * Searchable single select (Popover + Command). Arrow keys on the trigger open
 * the list, as they do on any combobox.
 */
export function SearchableSelect<T extends SearchableSelectOption>({
  options,
  value,
  onChange,
  placeholder = "Select",
  searchPlaceholder = "Search…",
  emptyText = "No matches.",
  formatOptionLabel,
  disabled = false,
  className
}: {
  options: T[];
  value: T | null;
  onChange: (option: T | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  formatOptionLabel?: (option: T) => React.ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const listId = React.useId();
  const renderLabel = (option: T) =>
    formatOptionLabel ? formatOptionLabel(option) : option.label;

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          disabled={disabled}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              setOpen(true);
            }
          }}
          className={cn(
            "h-8 w-full min-w-0 justify-between px-2.5 font-normal",
            !value && "text-fg-4",
            className
          )}
        >
          <span className="min-w-0 truncate">
            {value ? renderLabel(value) : placeholder}
          </span>
          <ChevronsUpDown className="text-fg-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) min-w-56 p-0"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList id={listId}>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label]}
                  disabled={option.isDisabled}
                  data-checked={option.value === value?.value || undefined}
                  onSelect={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {renderLabel(option)}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function reviewStatusTone(status?: string): StatusTone {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "accepted") return "success";
  if (normalized === "revision_requested") return "warn";
  return "neutral";
}

function formatEvidenceSelectOption(option: SourceSelectOption) {
  const exhibitPrefix = option.exhibit ? `${option.exhibit}:` : "";
  if (exhibitPrefix && option.label.startsWith(exhibitPrefix)) {
    return (
      <span>
        <span className="font-medium">{exhibitPrefix}</span>
        {option.label.slice(exhibitPrefix.length)}
      </span>
    );
  }

  const fallbackMatch = option.label.match(/^([^:]+:)(.*)$/);
  if (!fallbackMatch) return option.label;

  return (
    <span>
      <span className="font-medium">{fallbackMatch[1]}</span>
      {fallbackMatch[2]}
    </span>
  );
}

function formatIntakeSelectOption(option: SourceSelectOption) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-2">
      <span className="min-w-0 truncate">
        {option.intakeTitle || option.label}
      </span>
      <span className="flex flex-none items-center gap-1">
        {option.reviewStatus ? (
          <StatusBadge tone={reviewStatusTone(option.reviewStatus)}>
            {reviewStatusLabel(option.reviewStatus)}
          </StatusBadge>
        ) : null}
        {option.notReady ? <StatusBadge>Not Ready</StatusBadge> : null}
      </span>
    </div>
  );
}

function TypeformSourceTypeOption({
  label,
  icon,
  onClick
}: {
  label: React.ReactNode;
  icon: LucideIcon;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1 text-left text-base text-fg-2 transition-colors duration-100 outline-none hover:bg-fill-1 hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40"
      onClick={onClick}
    >
      <IconTile icon={icon} size="sm" />
      <span className="min-w-0 flex-1">{label}</span>
    </button>
  );
}

function SourceBuilderSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-140 min-w-0 flex-col gap-6">
      {Array.from({ length: 2 }).map((_, index) => (
        <div
          key={`source-builder-skeleton-${index}`}
          className="relative flex w-full min-w-0 items-stretch"
        >
          {index === 0 ? (
            <div className="absolute -bottom-6.5 left-5.75 top-9 w-px bg-line-2" />
          ) : null}
          <div className="relative z-1 flex w-full min-w-0 items-start gap-2.5 rounded-lg bg-elevated px-3 py-2 text-left ring-1 ring-line-2">
            <Skeleton className="size-6 flex-none rounded-full" />
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1 pt-1">
              <div className="flex w-full min-w-0 items-center gap-2">
                <Skeleton
                  className={cn("h-3", index === 0 ? "w-44" : "w-36")}
                />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <div className="flex flex-none items-center gap-1 pt-1">
              <Skeleton className="size-4 rounded-full" />
              <Skeleton className="size-4 rounded-full" />
              <Skeleton className="size-4 rounded-full" />
            </div>
          </div>
        </div>
      ))}
      <div className="flex w-full min-w-0 items-center justify-center gap-2 rounded-lg border border-dashed border-line-3 px-3 py-2.5">
        <Skeleton className="size-4 rounded-full" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
  );
}

export function SourceBuilderExplainerRail({
  onDismiss
}: {
  onDismiss: () => void;
}) {
  return (
    <aside className="relative z-0 hidden h-full w-70 flex-none flex-col justify-center border-l border-line-1 px-6 py-8 lg:flex xl:w-80">
      <h3 className="text-lg font-medium text-fg">Why Does This Matter?</h3>
      <p className="mt-2.5 text-sm text-fg-2">
        Add data sources in the order you want CaseBlink to trust them. The
        first source gets the first chance to fill the form.
      </p>
      <p className="mt-3 text-sm text-fg-2">
        Each later source only attempts fields that earlier sources could not
        fill. Earlier answers are carried forward, so later steps add missing
        information without replacing what was already found.
      </p>
      <p className="mt-3 text-sm text-fg-2">
        You can combine uploaded evidence, accepted intake submissions, and
        pasted notes.
      </p>
      <Button
        type="button"
        variant="secondary"
        className="mt-5 w-fit"
        onClick={onDismiss}
      >
        Hide This
      </Button>
    </aside>
  );
}

function SortableSourceStep({
  id,
  isLast,
  isDragDisabled,
  children
}: {
  id: string;
  isLast: boolean;
  isDragDisabled: boolean;
  children: React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id,
    disabled: isDragDisabled
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.72 : 1,
    zIndex: isDragging ? 3 : 0
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative flex w-full min-w-0 items-stretch"
    >
      {!isLast ? (
        <div className="absolute -bottom-6.5 left-5.75 top-9 w-px bg-line-2" />
      ) : null}
      <button
        ref={setActivatorNodeRef}
        type="button"
        aria-label="Drag source to reorder"
        disabled={isDragDisabled}
        className="absolute -left-9 top-1/2 z-2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-fg-3 transition-colors duration-100 outline-none hover:bg-fill-1 hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-40"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3.5" />
      </button>
      {children}
    </div>
  );
}

function EvidenceDocumentPdfPreview({
  individualId,
  document
}: {
  individualId: string;
  document: CasePrepEvidenceSource;
}) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = "";
    setPreviewUrl("");
    setHasError(false);
    if (!individualId || !document.document_id) return undefined;

    setIsLoading(true);
    DocumentsAPI.getDocumentPdf(individualId, document.document_id)
      .then((pdfBlob) => {
        if (!isMounted) return;
        objectUrl = URL.createObjectURL(pdfBlob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (!isMounted) return;
        setHasError(true);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [individualId, document.document_id]);

  return (
    <div className="relative aspect-8.5/11 w-full overflow-hidden rounded-md bg-fill-1">
      {isLoading ? (
        <LoadingState className="h-full px-5 py-6" text="Loading preview..." />
      ) : previewUrl ? (
        <PdfPreview
          file={previewUrl}
          ariaLabel={`${document.title} preview`}
          showControls={true}
        />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-5 py-6 text-center">
          <FileText className="size-5 text-fg-3" />
          <span className="text-sm text-fg-3">
            {hasError ? "Preview unavailable." : "No preview available."}
          </span>
        </div>
      )}
    </div>
  );
}

function selectedIntakeOption(
  intakes: CasePrepIntakeSource[],
  value: string
):
  | {
      intake: CasePrepIntakeSource;
      submission: CasePrepIntakeSubmissionSource;
    }
  | undefined {
  const [intakeId, submissionId] = value.split(":");
  if (!intakeId || !submissionId) return undefined;
  const intake = intakes.find((item) => item.outgoing_intake_id === intakeId);
  const submission = intake?.submissions.find(
    (item) => item.outgoing_intake_submission_id === submissionId
  );
  return intake && submission && !submission.disabled
    ? { intake, submission }
    : undefined;
}

function InlineAddSourceCard({
  stepNumber,
  individualId,
  onCancel,
  availableSources,
  onAdd,
  initialSource,
  submitLabel = "Add Source",
  canDismiss = true
}: {
  stepNumber: number;
  individualId: string;
  onCancel: () => void;
  availableSources: CasePrepUscisSourcesResponse;
  onAdd: (source: CasePrepUscisSource) => void;
  initialSource?: CasePrepUscisSource;
  submitLabel?: string;
  canDismiss?: boolean;
}) {
  const initialIntakeValue =
    initialSource?.source_type === "intake"
      ? `${
          initialSource.metadata?.outgoing_intake_id ??
          initialSource.source_id ??
          ""
        }:${initialSource.metadata?.outgoing_intake_submission_id ?? ""}`
      : "";
  const [sourceType, setSourceType] = useState<CasePrepUscisSourceType | null>(
    initialSource?.source_type ?? null
  );
  const [selectionStep, setSelectionStep] = useState<"type" | "options">(
    initialSource ? "options" : "type"
  );
  const [evidenceId, setEvidenceId] = useState(
    initialSource?.source_type === "evidence_document"
      ? initialSource.source_id ?? ""
      : ""
  );
  const [caseFileId, setCaseFileId] = useState(
    initialSource?.source_type === "case_file"
      ? initialSource.source_id ?? ""
      : ""
  );
  const [intakeValue, setIntakeValue] = useState(initialIntakeValue);
  const [freeText, setFreeText] = useState(
    initialSource?.source_type === "free_text"
      ? initialSource.free_text ?? ""
      : ""
  );
  const [showEvidencePreview, setShowEvidencePreview] = useState(false);
  const [showIntakePreview, setShowIntakePreview] = useState(false);
  const isEditing = Boolean(initialSource);

  const reset = () => {
    setSourceType(null);
    setSelectionStep("type");
    setEvidenceId("");
    setCaseFileId("");
    setIntakeValue("");
    setFreeText("");
    setShowEvidencePreview(false);
    setShowIntakePreview(false);
  };

  const caseFiles = availableSources.case_files ?? [];
  const selectedCaseFile = caseFiles.find(
    (file) => file.node_id === caseFileId
  );
  const caseFileOptions = useMemo<SourceSelectOption[]>(
    () =>
      caseFiles.map((file) => ({
        value: file.node_id,
        label: `${file.file_title || file.title}${
          file.disabled ? " (no readable content)" : ""
        }`,
        exhibit: file.exhibit_label,
        isDisabled: file.disabled
      })),
    [caseFiles]
  );
  const selectedCaseFileOption =
    caseFileOptions.find((option) => option.value === caseFileId) ?? null;

  const selectedEvidence = availableSources.evidence_documents.find(
    (doc) => doc.document_id === evidenceId
  );
  const evidenceOptions = useMemo<SourceSelectOption[]>(
    () =>
      availableSources.evidence_documents.map((doc) => ({
        value: doc.document_id,
        label: `${doc.title}${doc.disabled ? " (OCR unavailable)" : ""}`,
        exhibit: doc.exhibit,
        isDisabled: doc.disabled
      })),
    [availableSources.evidence_documents]
  );
  const selectedEvidenceOption =
    evidenceOptions.find((option) => option.value === evidenceId) ?? null;
  const intakeOptions = useMemo<SourceSelectOption[]>(
    () =>
      availableSources.intakes.flatMap((intake) =>
        intake.submissions.length > 0
          ? intake.submissions.map((submission) => ({
              value: `${intake.outgoing_intake_id}:${submission.outgoing_intake_submission_id}`,
              label: `${intake.title} ${reviewStatusLabel(
                submission.review_status
              )}${submission.disabled ? " Not Ready" : ""}`.trim(),
              intakeTitle: intake.title,
              reviewStatus: submission.review_status,
              notReady: submission.disabled,
              isDisabled: submission.disabled
            }))
          : [
              {
                value: intake.outgoing_intake_id,
                label: `${intake.title} (not completed)`,
                intakeTitle: intake.title,
                reviewStatus: "",
                notReady: true,
                isDisabled: true
              }
            ]
      ),
    [availableSources.intakes]
  );
  const selectedIntakeSourceOption =
    intakeOptions.find((option) => option.value === intakeValue) ?? null;
  const selectedIntake = selectedIntakeOption(
    availableSources.intakes,
    intakeValue
  );
  const selectedIntakeAnswers =
    selectedIntake?.submission.initial_intake_answers ?? [];

  const canAdd = Boolean(
    sourceType &&
      ((sourceType === "evidence_document" && selectedEvidence) ||
        (sourceType === "case_file" && selectedCaseFile) ||
        (sourceType === "intake" && selectedIntake) ||
        (sourceType === "free_text" && freeText.trim()))
  );

  const chooseSourceType = (nextSourceType: CasePrepUscisSourceType) => {
    setSourceType(nextSourceType);
    setSelectionStep("options");
    setEvidenceId("");
    setCaseFileId("");
    setIntakeValue("");
    setFreeText("");
    setShowEvidencePreview(false);
    setShowIntakePreview(false);
  };

  const add = () => {
    if (sourceType === "evidence_document" && selectedEvidence) {
      onAdd({
        source_type: "evidence_document",
        source_id: selectedEvidence.document_id,
        source_label: selectedEvidence.title
      });
      reset();
    } else if (sourceType === "case_file" && selectedCaseFile) {
      onAdd({
        source_type: "case_file",
        source_id: selectedCaseFile.node_id,
        source_label: selectedCaseFile.title
      });
      reset();
    } else if (sourceType === "intake" && selectedIntake) {
      onAdd({
        source_type: "intake",
        source_id: selectedIntake.intake.outgoing_intake_id,
        source_label: `${selectedIntake.intake.title} (${selectedIntake.submission.matched_field_count} answers)`,
        metadata: {
          outgoing_intake_id: selectedIntake.intake.outgoing_intake_id,
          outgoing_intake_submission_id:
            selectedIntake.submission.outgoing_intake_submission_id
        }
      });
      reset();
    } else if (sourceType === "free_text" && freeText.trim()) {
      onAdd({
        source_type: "free_text",
        source_label: "Pasted data",
        free_text: freeText.trim()
      });
      reset();
    }
  };

  return (
    <div className="relative flex w-full min-w-0 items-stretch">
      <div className="relative z-1 flex w-full min-w-0 items-start gap-2.5 rounded-lg border border-dashed border-line-3 bg-elevated px-3 py-4 text-left">
        {canDismiss ? (
          <div
            className="absolute right-3 top-3.5 z-3"
            aria-label="Remove source card"
          >
            <Button
              type="button"
              variant="danger"
              size="icon-xs"
              aria-label="Remove source card"
              onClick={() => {
                reset();
                onCancel();
              }}
            >
              <Trash2 />
            </Button>
          </div>
        ) : null}
        <div className="relative z-2 flex size-6 flex-none items-center justify-center rounded-full bg-accent-soft text-xs text-accent-seed">
          {stepNumber}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex min-w-0 flex-col gap-0.5 pt-0.5">
            <span className="text-base font-medium text-fg">
              {selectionStep === "type"
                ? "Add Data Source"
                : isEditing
                ? "Edit Data Source"
                : "Add Data Source"}
            </span>
            <span className="text-sm text-fg-3">
              {selectionStep === "type"
                ? "Choose a data source type."
                : sourceType === "case_file"
                ? "Choose the file from the case to use."
                : sourceType === "evidence_document"
                ? "Choose the piece of case evidence to use."
                : sourceType === "intake"
                ? "Choose the completed intake submission to use."
                : "Paste the facts, notes, or data you would like to provide."}
            </span>
          </div>

          {selectionStep === "type" ? (
            <div className="flex max-w-95 flex-col gap-1">
              {caseFiles.length > 0 ? (
                <TypeformSourceTypeOption
                  label={
                    <>
                      I want to use a{" "}
                      <span className="font-medium text-fg">case file</span>.
                    </>
                  }
                  icon={Folder}
                  onClick={() => chooseSourceType("case_file")}
                />
              ) : null}
              {availableSources.evidence_documents.length > 0 ||
              caseFiles.length === 0 ? (
                <TypeformSourceTypeOption
                  label={
                    <>
                      I want to use{" "}
                      <span className="font-medium text-fg">
                        uploaded evidence
                      </span>{" "}
                      from this case.
                    </>
                  }
                  icon={FileText}
                  onClick={() => chooseSourceType("evidence_document")}
                />
              ) : null}
              <TypeformSourceTypeOption
                label={
                  <>
                    I want to use an{" "}
                    <span className="font-medium text-fg">
                      intake submission
                    </span>
                    .
                  </>
                }
                icon={ClipboardList}
                onClick={() => chooseSourceType("intake")}
              />
              <TypeformSourceTypeOption
                label={
                  <>
                    I want to <span className="font-medium text-fg">paste</span>{" "}
                    in information I have on hand.
                  </>
                }
                icon={AlignLeft}
                onClick={() => chooseSourceType("free_text")}
              />
            </div>
          ) : (
            <div className="flex max-w-115 flex-col gap-3">
              {sourceType === "case_file" ? (
                <SearchableSelect<SourceSelectOption>
                  options={caseFileOptions}
                  value={selectedCaseFileOption}
                  placeholder="Select a case file"
                  formatOptionLabel={formatEvidenceSelectOption}
                  onChange={(option) => setCaseFileId(option?.value ?? "")}
                />
              ) : sourceType === "evidence_document" ? (
                <>
                  <SearchableSelect<SourceSelectOption>
                    options={evidenceOptions}
                    value={selectedEvidenceOption}
                    placeholder="Select evidence"
                    formatOptionLabel={formatEvidenceSelectOption}
                    onChange={(option) => {
                      setEvidenceId(option?.value ?? "");
                      setShowEvidencePreview(false);
                    }}
                  />
                  {selectedEvidence ? (
                    <>
                      <Button
                        type="button"
                        variant="link"
                        className="w-fit text-sm"
                        onClick={() =>
                          setShowEvidencePreview((current) => !current)
                        }
                      >
                        {showEvidencePreview
                          ? "Hide Evidence"
                          : "Show Evidence"}
                      </Button>
                      {showEvidencePreview ? (
                        <EvidenceDocumentPdfPreview
                          individualId={individualId}
                          document={selectedEvidence}
                        />
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : sourceType === "intake" ? (
                <>
                  <SearchableSelect<SourceSelectOption>
                    options={intakeOptions}
                    value={selectedIntakeSourceOption}
                    placeholder="Select intake"
                    formatOptionLabel={formatIntakeSelectOption}
                    onChange={(option) => {
                      setIntakeValue(option?.value ?? "");
                      setShowIntakePreview(false);
                    }}
                  />
                  {selectedIntake ? (
                    <>
                      <Button
                        type="button"
                        variant="link"
                        className="w-fit text-sm"
                        onClick={() =>
                          setShowIntakePreview((current) => !current)
                        }
                      >
                        {showIntakePreview ? "Hide Evidence" : "Show Evidence"}
                      </Button>
                      {showIntakePreview ? (
                        <div className="max-h-65 overflow-auto rounded-md bg-fill-1">
                          {selectedIntakeAnswers.length > 0 ? (
                            <table className="w-full border-collapse text-left">
                              <thead className="sticky top-0 bg-chrome">
                                <tr>
                                  <th className="w-1/2 border-b border-line-2 px-3 py-2 text-sm font-normal text-fg-2">
                                    Question
                                  </th>
                                  <th className="border-b border-line-2 px-3 py-2 text-sm font-normal text-fg-2">
                                    Value
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedIntakeAnswers.map((answer, index) => {
                                  const value =
                                    answer.answer_text ||
                                    stringifyValue(answer.answer);
                                  return (
                                    <tr
                                      key={`intake-answer-${
                                        answer.question_id || index
                                      }`}
                                      className="border-b border-line-1 last:border-b-0"
                                    >
                                      <td className="align-top px-3 py-2 text-sm font-medium text-fg">
                                        {answer.question ||
                                          answer.question_id ||
                                          "Question"}
                                      </td>
                                      <td className="whitespace-pre-wrap align-top px-3 py-2 text-sm text-fg">
                                        {value || "-"}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          ) : (
                            <div className="px-3 py-2 text-sm text-fg-3">
                              No question-value pairs are available for this
                              intake submission.
                            </div>
                          )}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : (
                <Textarea
                  className="min-h-45"
                  value={freeText}
                  onChange={(event) => setFreeText(event.target.value)}
                  placeholder="Paste facts, notes, or structured data to use for this step."
                />
              )}
            </div>
          )}

          <div className="flex max-w-115 flex-wrap justify-end gap-3">
            {selectionStep === "options" ? (
              <Button
                type="button"
                variant="secondary"
                disabled={!canAdd}
                onClick={add}
              >
                {submitLabel}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Loads the evidence and intakes a form can be filled from, and optionally asks
 * the recommender for a ranked starting list.
 *
 * ``recommend`` is read only at the moment the candidate list arrives, so a list
 * the attorney edits afterwards never triggers a second recommendation.
 */
export function useCandidateSources({
  individualId,
  caseId,
  formNumber,
  recommend,
  onRecommendationsRequested,
  onRecommendations,
  onError
}: {
  individualId: string;
  caseId: string;
  formNumber: string;
  recommend: boolean;
  onRecommendationsRequested?: () => void;
  onRecommendations?: (recommendations: CasePrepUscisSource[]) => void;
  onError?: (error: unknown) => void;
}): {
  availableSources: CasePrepUscisSourcesResponse;
  isLoadingSources: boolean;
  isRecommendingSources: boolean;
} {
  const [availableSources, setAvailableSources] =
    useState<CasePrepUscisSourcesResponse>(EMPTY_CANDIDATE_SOURCES);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [isRecommendingSources, setIsRecommendingSources] = useState(false);
  const latest = useRef({
    recommend,
    onRecommendationsRequested,
    onRecommendations,
    onError
  });

  useEffect(() => {
    latest.current = {
      recommend,
      onRecommendationsRequested,
      onRecommendations,
      onError
    };
  });

  useEffect(() => {
    if (!formNumber) return undefined;
    let isMounted = true;
    setIsLoadingSources(true);
    CasePrepUscisFormsAPI.listSources(individualId, {
      case_id: caseId,
      form_number: formNumber
    })
      .then((response) => {
        if (!isMounted) return;
        setAvailableSources(response);
        if (!latest.current.recommend) return;
        latest.current.onRecommendationsRequested?.();
        setIsRecommendingSources(true);
        CasePrepUscisFormsAPI.recommendSources(individualId, {
          case_id: caseId,
          form_number: formNumber
        })
          .then((recommendations) => {
            if (!isMounted || recommendations.length === 0) return;
            latest.current.onRecommendations?.(recommendations);
          })
          // A failed recommendation just leaves the attorney adding sources by
          // hand, which is the pre-existing flow. No need to interrupt them.
          .catch(() => undefined)
          .finally(() => {
            if (isMounted) setIsRecommendingSources(false);
          });
      })
      .catch((error) => {
        if (!isMounted) return;
        latest.current.onError?.(error);
      })
      .finally(() => {
        if (isMounted) setIsLoadingSources(false);
      });
    return () => {
      isMounted = false;
    };
  }, [caseId, formNumber, individualId]);

  return { availableSources, isLoadingSources, isRecommendingSources };
}

/**
 * The ordered list of data sources a form should be filled from.
 *
 * Renders as two stacked blocks - an optional header with the list controls, and
 * the scrollable list itself - so a parent laying out a flex column gets the
 * same result whether it is the creation wizard or the autofill modal.
 */
export function SourceListEditor({
  individualId,
  caseId,
  formNumber,
  sources,
  onChange,
  disabled = false,
  recommend,
  onRecommendationsRequested,
  header,
  onStatusChange
}: {
  individualId: string;
  caseId: string;
  formNumber: string;
  sources: CasePrepUscisSource[];
  onChange: (sources: CasePrepUscisSource[]) => void;
  disabled?: boolean;
  recommend: boolean;
  onRecommendationsRequested?: () => void;
  header?: React.ReactNode;
  onStatusChange?: (status: SourceListEditorStatus) => void;
}) {
  const [isAddingSource, setIsAddingSource] = useState(sources.length === 0);
  const [editingSourceIndexes, setEditingSourceIndexes] = useState<Set<number>>(
    () => new Set()
  );
  const previousSourceCountRef = useRef(sources.length);
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;

  const { availableSources, isLoadingSources, isRecommendingSources } =
    useCandidateSources({
      individualId,
      caseId,
      formNumber,
      recommend,
      onRecommendationsRequested,
      onRecommendations: (recommendations) => {
        if (sourcesRef.current.length > 0) return;
        onChange(recommendations);
      },
      onError: (error) => {
        toast.error("Unable to load data sources", {
          description: error instanceof Error ? error.message : undefined
        });
      }
    });

  // A list filled from outside - recommendations, or a form's saved steps -
  // answers the question the empty-state add card was asking.
  useEffect(() => {
    if (sources.length > 0 && previousSourceCountRef.current === 0) {
      setIsAddingSource(false);
    }
    previousSourceCountRef.current = sources.length;
  }, [sources.length]);

  const hasOpenDraft = isAddingSource || editingSourceIndexes.size > 0;
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  useEffect(() => {
    onStatusChangeRef.current?.({
      isLoadingSources,
      isRecommendingSources,
      hasOpenDraft
    });
  }, [hasOpenDraft, isLoadingSources, isRecommendingSources]);

  const sourceSortableIds = useMemo(
    () =>
      sources.map(
        (source, index) => `${sourceRenderKey(source)}:${index.toString()}`
      ),
    [sources]
  );
  const sourceDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  const handleSourceDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sourceSortableIds.indexOf(String(active.id));
    const newIndex = sourceSortableIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    onChange(arrayMove(sources, oldIndex, newIndex));
  };
  const stopEditingSource = (index: number) => {
    setEditingSourceIndexes((current) => {
      const next = new Set(current);
      next.delete(index);
      return next;
    });
  };
  const showInlineAddSource =
    sources.length < MAX_FORM_SOURCES &&
    (isAddingSource || sources.length === 0);

  return (
    <>
      {header ? (
        <div className="mx-auto mb-5 w-full max-w-140">{header}</div>
      ) : null}
      <div className="flex min-h-0 flex-1 items-start overflow-x-visible overflow-y-auto pb-2">
        {isLoadingSources || isRecommendingSources ? (
          <div className="mx-auto flex w-full max-w-155 min-w-0 flex-col gap-5 pl-10">
            <div className="flex items-center gap-2.5 self-center rounded-full bg-accent-soft px-3.5 py-2">
              <Spinner className="size-3.5 text-accent-seed" />
              <span className="text-sm font-medium text-fg-2">
                {isRecommendingSources
                  ? `Looking through this case for sources that can fill ${formNumber}…`
                  : "Gathering the evidence and intakes on this case…"}
              </span>
            </div>
            <SourceBuilderSkeleton />
          </div>
        ) : (
          <DndContext
            sensors={sourceDragSensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleSourceDragEnd}
          >
            <SortableContext
              items={sourceSortableIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="mx-auto flex w-full max-w-155 min-w-0 flex-col gap-6 pl-10">
                {sources.map((source, index) => {
                  const isEditingSource = editingSourceIndexes.has(index);
                  const isLast =
                    index === sources.length - 1 && !showInlineAddSource;
                  const sortableId = sourceSortableIds[index];
                  return (
                    <SortableSourceStep
                      key={sortableId}
                      id={sortableId}
                      isLast={isLast}
                      isDragDisabled={disabled || hasOpenDraft}
                    >
                      {isEditingSource ? (
                        <InlineAddSourceCard
                          stepNumber={index + 1}
                          individualId={individualId}
                          availableSources={availableSources}
                          initialSource={source}
                          submitLabel="Save"
                          canDismiss
                          onCancel={() => stopEditingSource(index)}
                          onAdd={(updatedSource) => {
                            onChange(
                              sources.map((item, itemIndex) =>
                                itemIndex === index ? updatedSource : item
                              )
                            );
                            stopEditingSource(index);
                          }}
                        />
                      ) : (
                        <div className="relative z-1 flex w-full min-w-0 items-start gap-2.5 rounded-lg bg-elevated px-3 py-2 text-left ring-1 ring-line-2">
                          <div className="relative z-2 flex size-6 flex-none items-center justify-center rounded-full bg-fill-2 text-xs text-fg-3">
                            {index + 1}
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col items-start gap-1 pt-0.5">
                            <div className="flex w-full min-w-0 items-center gap-2">
                              <span className="min-w-0 truncate text-base text-fg">
                                <span className="font-medium">
                                  {sourceStepLeadIn(index, sources.length)}
                                </span>{" "}
                                {sourceCardLabel(source)}
                              </span>
                              <SourceTypeTag type={source.source_type} />
                              {isRecommendedSource(source) ? (
                                <StatusBadge tone="accent">
                                  Recommended
                                </StatusBadge>
                              ) : null}
                            </div>
                            {sourceRecommendationReason(source) ? (
                              <span className="w-full text-sm text-fg-3">
                                {sourceRecommendationReason(source)}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex flex-none items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-xs"
                              aria-label="Edit source"
                              disabled={disabled || isAddingSource}
                              onClick={() => {
                                setIsAddingSource(false);
                                setEditingSourceIndexes((current) => {
                                  const next = new Set(current);
                                  next.add(index);
                                  return next;
                                });
                              }}
                            >
                              <Pencil />
                            </Button>
                            <Button
                              type="button"
                              variant="danger"
                              size="icon-xs"
                              aria-label="Delete source"
                              disabled={disabled || hasOpenDraft}
                              onClick={() =>
                                onChange(sources.filter((_, i) => i !== index))
                              }
                            >
                              <Trash2 />
                            </Button>
                          </div>
                        </div>
                      )}
                    </SortableSourceStep>
                  );
                })}
                {showInlineAddSource ? (
                  <InlineAddSourceCard
                    stepNumber={sources.length + 1}
                    individualId={individualId}
                    availableSources={availableSources}
                    canDismiss={sources.length > 0}
                    onCancel={() => setIsAddingSource(false)}
                    onAdd={(source) => {
                      if (sources.length < MAX_FORM_SOURCES) {
                        onChange([...sources, source]);
                      }
                      setIsAddingSource(false);
                    }}
                  />
                ) : null}
                {!showInlineAddSource && sources.length < MAX_FORM_SOURCES ? (
                  <button
                    type="button"
                    className="flex w-full min-w-0 items-center justify-center gap-2 rounded-lg border border-dashed border-line-3 px-3 py-2.5 text-base text-fg-2 transition-colors duration-100 outline-none hover:bg-fill-1 hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={isLoadingSources || disabled}
                    onClick={() => setIsAddingSource(true)}
                  >
                    <Plus className="size-3.5" />
                    Add Data Source
                  </button>
                ) : null}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </>
  );
}
